/**
 * EffectsChain — owns the post-voice effect modules and their ordering.
 *
 * The audio graph is always voiceBus → effect[0] → effect[1] → ... →
 * effect[last] → master. Reordering, adding, or removing tears down
 * every edge and re-stitches the chain.
 *
 * Order is stored as a list of effect *instance ids* (not types) so
 * the chain can hold multiple effects of the same type — e.g. two
 * distortions, three delays. `addEffect(type)` mints a fresh
 * instance via the registry; `removeEffect(id)` disposes and
 * disconnects.
 *
 * Subscribers fire whenever the chain changes — the UI uses this to
 * re-sort the draggable list of effect panels and refresh the mod
 * matrix's target list when an effect is added or removed.
 */

import type { AudioEngine } from './AudioEngine';
import type { EffectRegistry } from './effects/registry';
import type { SynthModule } from './modules/SynthModule';

export interface EffectModule extends SynthModule {
  readonly input: AudioNode;
  readonly output: AudioNode;
}

/**
 * Listener payload — current ordered list of effect modules. The chain
 * passes the live array shape (modules) so subscribers can read both
 * id and type without re-querying.
 */
export type EffectsChainListener = (modules: readonly EffectModule[]) => void;

export interface EffectsChainOptions {
  /** Registry used by addEffect/createFromState to mint instances. */
  readonly registry: EffectRegistry;
  /** Initial effect modules — already constructed, ordered. */
  readonly initial?: readonly EffectModule[];
}

export class EffectsChain {
  private engine: AudioEngine | null = null;
  private modules: EffectModule[];
  private readonly listeners = new Set<EffectsChainListener>();
  private readonly registry: EffectRegistry;

  constructor(opts: EffectsChainOptions) {
    this.registry = opts.registry;
    this.modules = opts.initial ? [...opts.initial] : [];
  }

  /** Stable ordered list of effect instance ids. */
  getOrder(): readonly string[] {
    return this.modules.map((m) => m.id);
  }

  /** Every effect instance in current order. */
  getModules(): readonly EffectModule[] {
    return this.modules;
  }

  /** Map type → count, for "DIST 1 / DIST 2" disambiguation in mod targets. */
  countByType(): Map<string, number> {
    const out = new Map<string, number>();
    for (const m of this.modules) out.set(m.type, (out.get(m.type) ?? 0) + 1);
    return out;
  }

  /**
   * Activate the chain with a live engine. Initializes any modules
   * that were added before the engine became live (palette adds while
   * audio was still locked) so their input/output AudioNodes exist
   * before rewire wires them up. `init()` is idempotent across all
   * modules — calling it on an already-started one is a no-op — so
   * this is safe even when most modules were init'd by the caller
   * already.
   */
  setEngine(engine: AudioEngine): void {
    this.engine = engine;
    for (const m of this.modules) {
      try {
        m.init(engine);
      } catch (err) {
        // A failed init shouldn't abort the entire chain bootstrap; log
        // and continue. The rewire below will skip nodes whose I/O is
        // still null (defensive — should be rare in practice).
        console.warn('[effects] init failed for', m.id, err);
      }
    }
    this.rewire();
  }

  /**
   * Reorder the chain by id. Unknown ids are ignored; known modules
   * absent from `next` get appended in their current relative order.
   * Rewires the audio graph and fires listeners on actual change.
   */
  setOrder(next: readonly string[]): void {
    const byId = new Map(this.modules.map((m) => [m.id, m]));
    const reordered: EffectModule[] = [];
    const seen = new Set<string>();
    for (const id of next) {
      const m = byId.get(id);
      if (m && !seen.has(id)) {
        reordered.push(m);
        seen.add(id);
      }
    }
    for (const m of this.modules) {
      if (!seen.has(m.id)) reordered.push(m);
    }
    const same =
      reordered.length === this.modules.length &&
      reordered.every((m, i) => m === this.modules[i]);
    if (same) return;
    this.modules = reordered;
    this.rewire();
    this.emit();
  }

  /** Move the module at `fromIndex` to `toIndex`, clamping both into range. */
  move(fromIndex: number, toIndex: number): void {
    const clamp = (n: number) => Math.max(0, Math.min(this.modules.length - 1, n));
    const from = clamp(fromIndex);
    const to = clamp(toIndex);
    if (from === to) return;
    const next = [...this.modules];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    this.setOrder(next.map((m) => m.id));
  }

  /**
   * Mint a new effect instance of `type`, init it on the live engine
   * (if any), and append it to the chain. Returns the new module so
   * callers can wire UI / mod targets to it. `null` if `type` isn't
   * registered.
   *
   * `at` (optional) inserts at a specific index instead of appending.
   */
  addEffect(type: string, at?: number): EffectModule | null {
    const instance = this.registry.create(type);
    if (!instance) return null;
    if (this.engine) instance.init(this.engine);
    if (typeof at === 'number') {
      const i = Math.max(0, Math.min(this.modules.length, at));
      this.modules.splice(i, 0, instance);
    } else {
      this.modules.push(instance);
    }
    this.rewire();
    this.emit();
    return instance;
  }

  /**
   * Adopt an already-constructed effect (e.g., during preset load).
   * The chain takes ownership: future remove calls dispose it. The
   * caller is responsible for `init`-ing it before adoption when the
   * engine is already live.
   */
  adoptEffect(instance: EffectModule, at?: number): void {
    if (typeof at === 'number') {
      const i = Math.max(0, Math.min(this.modules.length, at));
      this.modules.splice(i, 0, instance);
    } else {
      this.modules.push(instance);
    }
    this.rewire();
    this.emit();
  }

  /**
   * Remove the effect with `id`, dispose its audio resources, rewire.
   * No-op if the id is unknown. Returns true if a module was removed.
   */
  removeEffect(id: string): boolean {
    const idx = this.modules.findIndex((m) => m.id === id);
    if (idx < 0) return false;
    const [removed] = this.modules.splice(idx, 1);
    if (!removed) return false;
    try {
      removed.dispose();
    } catch {
      /* dispose threw — log later, don't abort the chain rebuild */
    }
    this.rewire();
    this.emit();
    return true;
  }

  /**
   * Replace the entire effect set in one shot. Disposes anything that
   * isn't kept; adopts new instances at their requested position.
   * Used by preset load to swap to a different chain composition.
   */
  replaceAll(next: readonly EffectModule[]): void {
    const keep = new Set(next.map((m) => m.id));
    for (const m of this.modules) {
      if (!keep.has(m.id)) {
        try {
          m.dispose();
        } catch {
          /* same as removeEffect — defensive */
        }
      }
    }
    this.modules = [...next];
    this.rewire();
    this.emit();
  }

  /** Look up an effect by id. */
  findById(id: string): EffectModule | undefined {
    return this.modules.find((m) => m.id === id);
  }

  /** Snapshot of the chain state for presets — type, id, params, bypass. */
  serialize(): Array<{
    type: string;
    id: string;
    bypass: boolean;
    params: Record<string, number | string>;
  }> {
    return this.modules.map((m) => {
      const s = m.serialize();
      return {
        type: m.type,
        id: m.id,
        bypass: !!s.bypass,
        params: { ...s.params },
      };
    });
  }

  /**
   * Replace the chain composition from a preset's effects list. Tears
   * down every existing effect (dispose), then mints fresh instances
   * via the registry using the saved type/id and inits them on the
   * live engine. Each instance's params are deserialized so the loaded
   * patch sounds the same.
   *
   * If the engine isn't set yet, instances are constructed but not
   * `init`-ed; the next `setEngine` call will rewire them but they
   * still need init. Callers responsible for ensuring engine readiness
   * before applying user-loaded presets.
   *
   * Unknown effect types in the list are skipped silently — that lets
   * presets saved with experimental effects degrade rather than break.
   */
  applyPreset(
    list: ReadonlyArray<{
      type: string;
      id: string;
      bypass?: boolean;
      params: Record<string, number | string>;
    }>,
  ): void {
    // Drop everything currently in the chain. Disconnect from the
    // graph first so the rebuild step doesn't have to fight stale
    // edges; dispose each module so worklets / oscillators stop.
    if (this.engine) {
      try {
        this.engine.voiceBus.disconnect();
      } catch {
        /* already detached */
      }
    }
    for (const m of this.modules) {
      try {
        m.dispose();
      } catch {
        /* defensive — preset load shouldn't bail if a single dispose throws */
      }
    }
    this.modules = [];

    // Recreate from the saved list. Each entry's id is reused so any
    // mod connections referencing the old chain still target the new
    // instance.
    const next: EffectModule[] = [];
    for (const entry of list) {
      const instance = this.registry.create(entry.type, entry.id);
      if (!instance) continue;
      if (entry.bypass !== undefined) instance.bypass = entry.bypass;
      if (this.engine) instance.init(this.engine);
      // Push state through the module's deserialize so its parameters
      // run their subscribe chains (Drive curve rebuilds, mix gains
      // update, etc.) just like loading a preset does for any module.
      instance.deserialize({
        id: entry.id,
        type: entry.type,
        ...(entry.bypass !== undefined ? { bypass: entry.bypass } : {}),
        params: entry.params,
      });
      next.push(instance);
    }
    this.modules = next;
    this.rewire();
    this.emit();
  }

  subscribe(fn: EffectsChainListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Toggle bypass on a single effect by id. Rewires the chain so the
   * audio graph routes around bypassed effects entirely (no spent
   * cycles on a node whose output isn't audible). State is persisted
   * with the preset since `bypass` is already in the serialized
   * shape. No-op when the id isn't in the chain.
   */
  setBypass(id: string, bypassed: boolean): void {
    const m = this.modules.find((x) => x.id === id);
    if (!m) return;
    if (m.bypass === bypassed) return;
    m.bypass = bypassed;
    this.rewire();
    this.emit();
  }

  private rewire(): void {
    const engine = this.engine;
    if (!engine) return;
    const voiceBus = engine.voiceBus;
    const master = engine.master;
    try {
      voiceBus.disconnect();
    } catch {
      /* already disconnected */
    }
    // Filter out any modules whose I/O hasn't been hooked up yet — a
    // module added before setEngine and never init'd will have null
    // input/output, which would explode the connect() calls below.
    // setEngine inits everything proactively, but rewire is also
    // called from setOrder / addEffect / removeEffect, and a defensive
    // skip keeps a single broken module from breaking the whole graph.
    // Bypassed modules are also skipped — the chain routes straight
    // past them so a switched-off effect adds zero audio cost.
    for (const m of this.modules) {
      if (!m.input || !m.output) continue;
      try {
        m.output.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    const wirable = this.modules.filter(
      (m) => m.input && m.output && !m.bypass,
    );
    if (wirable.length === 0) {
      voiceBus.connect(master);
      return;
    }
    voiceBus.connect(wirable[0]!.input);
    for (let i = 0; i < wirable.length - 1; i++) {
      wirable[i]!.output.connect(wirable[i + 1]!.input);
    }
    wirable[wirable.length - 1]!.output.connect(master);
  }

  private emit(): void {
    const snapshot = [...this.modules];
    for (const fn of this.listeners) fn(snapshot);
  }
}
